import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { SectionHeader, Card, Badge, SkillBar, AnimatedSection, StaggerContainer, StaggerItem } from '../components/common';
import { personalInfo, skills } from '../data/profileData';

const Skills = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Skills' },
    { id: 'Programming', name: 'Programming' },
    { id: 'Enterprise Solutions', name: 'Enterprise' },
    { id: 'Cloud Computing', name: 'Cloud' },
    { id: 'Security', name: 'Security' },
    { id: 'Infrastructure', name: 'Infrastructure' },
    { id: 'Analytics', name: 'Analytics' },
  ];

  const filteredSkills = activeCategory === 'all'
    ? skills.technical
    : skills.technical.filter(skill => skill.category === activeCategory);

  return (
    <>
      <Helmet>
        <title>Skills | Rakesh Kumar Tripathi</title>
        <meta name="description" content={`Technical skills and expertise of ${personalInfo.name}. Proficient in Linux, ERPNext, Python, Cybersecurity, Cloud Computing, and more.`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-8 pb-8 gradient-bg-subtle">
        <div className="container-custom">
          <AnimatedSection>
            <SectionHeader
              badge="Technical Expertise"
              title="Skills & Technologies"
              subtitle="Comprehensive expertise across programming, enterprise solutions, cloud computing, and cybersecurity built over 21+ years."
            />
          </AnimatedSection>
        </div>
      </section>

      {/* Technical Skills Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-bold text-dark-800 mb-8 text-center">
            Technical Proficiencies
          </h2>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-primary-500 text-white shadow-lg'
                    : 'bg-gray-100 text-dark-600 hover:bg-gray-200'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category.name}
              </motion.button>
            ))}
          </div>

          {/* Skills Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              className="grid md:grid-cols-2 gap-x-12 gap-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {filteredSkills.map((skill, index) => (
                <motion.div
                  key={skill.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <SkillBar
                    name={skill.name}
                    level={skill.level}
                    color="mixed"
                  />
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Skill Categories Section */}
      <section className="section-padding gradient-bg-subtle">
        <div className="container-custom">
          <SectionHeader
            badge="Expertise Areas"
            title="Skill Categories"
            subtitle="Organized overview of my technical capabilities"
          />

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skills.categories.map((category, index) => (
              <StaggerItem key={category.name}>
                <Card elevated className="h-full">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-lg flex items-center justify-center">
                      <span className="text-lg font-bold text-primary-500">
                        {category.name.charAt(0)}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-dark-800">{category.name}</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {category.skills.map((skill) => (
                      <Badge key={skill} variant="outline" size="sm">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </Card>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Soft Skills Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader
            badge="Beyond Technical"
            title="Soft Skills"
            subtitle="Leadership and interpersonal skills that complement technical expertise"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {skills.soft.map((skill, index) => (
              <motion.div
                key={skill.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="text-center" hover>
                  <div className="relative mb-4">
                    <svg className="w-24 h-24 mx-auto transform -rotate-90">
                      <circle
                        cx="48"
                        cy="48"
                        r="40"
                        stroke="#e5e7eb"
                        strokeWidth="8"
                        fill="none"
                      />
                      <motion.circle
                        cx="48"
                        cy="48"
                        r="40"
                        stroke="url(#gradient)"
                        strokeWidth="8"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={251.2}
                        initial={{ strokeDashoffset: 251.2 }}
                        whileInView={{ strokeDashoffset: 251.2 - (251.2 * skill.level) / 100 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.5, ease: 'easeOut' }}
                      />
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#0066cc" />
                          <stop offset="100%" stopColor="#00d9a0" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xl font-bold text-dark-800">{skill.level}%</span>
                    </div>
                  </div>
                  <h3 className="font-semibold text-dark-800">{skill.name}</h3>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Technologies Section */}
      <section className="section-padding bg-dark-800">
        <div className="container-custom">
          <SectionHeader
            badge="Tools"
            title="Tools & Technologies"
            subtitle="Software and platforms I work with regularly"
            titleClassName="text-white"
            subtitleClassName="text-gray-400"
          />

          <motion.div
            className="flex flex-wrap justify-center gap-3"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {skills.tools.map((tool, index) => (
              <motion.span
                key={tool}
                className="px-4 py-2 bg-dark-700 rounded-lg text-gray-300 hover:bg-primary-500 hover:text-white transition-colors cursor-default"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
                whileHover={{ scale: 1.05 }}
              >
                {tool}
              </motion.span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Certifications Highlight */}
      <section className="py-16 bg-gradient-to-r from-primary-500 to-secondary-500">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Certified Professional
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto mb-8">
              Microsoft Certified Professional (MCP) in Visual Basic 5.0 and STC certified
              in High Performance Computing & Enterprise Computing from IIT Kharagpur.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-white/20 backdrop-blur rounded-lg px-6 py-3">
                <span className="text-white font-medium">Microsoft Certified</span>
              </div>
              <div className="bg-white/20 backdrop-blur rounded-lg px-6 py-3">
                <span className="text-white font-medium">IIT Kharagpur Certified</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Skills;
